import { Box, Collapse } from '@mui/material';
import type { CategoryId } from '@timelog/pm-shared';
import {
  addSplitRow,
  getCategory,
  removeSplitRow,
  renameSplitRow,
  setSplitRowAmount,
  splitAmounts,
} from '@timelog/pm-shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { parseAmountInput, useContractEditing, useFinancials } from './editor';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { InlineTextCell } from '@/components/planning/cells';
import { formatAmountAbbrev, formatAmount } from '@/lib';

/**
 * The exp/mat "Split" section (CALC-17, BR-19 informational): collapsible
 * rows under the card — name + amount (typed amounts rebase to sharePct) +
 * the share chip. Over-allocation (>100%) is a soft warning, never a reject.
 */
export const SplitRowsSection = ({ categoryId }: { categoryId: CategoryId }) => {
  const { state, dispatch } = useFinancials();
  const { isEditing, startEditing, stopEditing } = useContractEditing();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('pmFinancials');

  const category = getCategory(state.document, categoryId);
  const split = splitAmounts(category);
  const rowCount = split.rows.length;
  const accent = categoryId === 'exp' ? 'var(--color-blue-60)' : 'var(--color-black-60)';

  /**
   * The v8 keyboard path through the rows: name → amount → next row's name;
   * Enter/Tab past the LAST amount adds the next line and lands on its name —
   * dozens of lines without touching the mouse. Shift-Tab walks back.
   */
  const addRow = () => {
    const id = crypto.randomUUID();
    dispatch({
      type: 'APPLY_OP',
      op: (document) => addSplitRow(document, categoryId, { id, name: t('splitRows.DefaultLine') }),
    });
    return id;
  };

  const advanceRows = (kind: 'name' | 'amount', rowId: string, intent: string) => {
    if (intent !== 'enter-next' && intent !== 'tab-next' && intent !== 'tab-prev') {
      stopEditing();
      return;
    }
    const ids = split.rows.map((row) => row.id);
    const index = ids.indexOf(rowId);
    if (intent === 'tab-prev') {
      if (kind === 'amount') startEditing(`split-name:${categoryId}:${rowId}`);
      else if (index > 0) startEditing(`split-amount:${categoryId}:${ids[index - 1]}`);
      else stopEditing();
      return;
    }
    if (kind === 'name') {
      startEditing(`split-amount:${categoryId}:${rowId}`);
      return;
    }
    const nextId = ids[index + 1];
    startEditing(`split-name:${categoryId}:${nextId ?? addRow()}`);
  };

  if (category.billedVia) return null;

  return (
    <Box>
      <Box sx={{ px: 2, pb: 1.5 }}>
        <Box
          component="button"
          type="button"
          data-automation-id={`split-toggle-${categoryId}`}
          onClick={() => setOpen((value) => !value)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            border: `1px solid ${open ? accent : 'var(--border-divider)'}`,
            borderRadius: 'var(--border-radius-pill)',
            bgcolor: open ? 'var(--bg-sunken)' : 'transparent',
            fontSize: 11,
            fontFamily: 'var(--font-family)',
            color: open ? 'var(--fg-1)' : 'var(--fg-3)',
            cursor: 'pointer',
          }}
        >
          <MaterialSymbol name={open ? 'unfold_less' : 'unfold_more'} size={13} />
          {open
            ? t('actions.Collapse')
            : rowCount > 0
              ? t('splitRows.SplitCount', { n: rowCount })
              : t('splitRows.Split')}
        </Box>
      </Box>

      <Collapse in={open}>
        <Box
          sx={{
            borderTop: 'var(--border-width-thin) solid var(--border-divider)',
            bgcolor: 'var(--bg-sunken)',
            px: 2,
            pt: 1,
            pb: 1.5,
          }}
          data-automation-id={`split-rows-${categoryId}`}
        >
          {split.overAllocated ? (
            <Box
              data-automation-id="split-over-allocated"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: 11,
                color: 'var(--color-warning-100)',
                mb: 1,
              }}
            >
              <MaterialSymbol name="warning" size={13} color="var(--color-warning-80)" />
              {t('splitRows.OverAllocated')}
            </Box>
          ) : null}

          {split.rows.map((row) => (
            <Box
              key={row.id}
              data-automation-id="split-row"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
                borderBottom: 'var(--border-width-thin) solid var(--border-divider)',
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  bgcolor: accent,
                  flex: 'none',
                  opacity: 0.7,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <InlineTextCell
                  automationId="split-row-name"
                  value={row.name}
                  editing={isEditing(`split-name:${categoryId}:${row.id}`)}
                  onStartEdit={() => startEditing(`split-name:${categoryId}:${row.id}`)}
                  onCancel={stopEditing}
                  advanceOnEnter
                  onCommit={(raw, intent) => {
                    const name = raw.trim();
                    if (name !== '') {
                      dispatch({
                        type: 'APPLY_OP',
                        op: (document) => renameSplitRow(document, categoryId, row.id, name),
                      });
                    }
                    advanceRows('name', row.id, intent);
                  }}
                  display={
                    <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-2)' }}>
                      {row.name}
                    </Box>
                  }
                />
              </Box>
              <InlineTextCell
                automationId="split-row-amount"
                value={row.amount > 0 ? formatAmount(Math.round(row.amount)) : ''}
                editing={isEditing(`split-amount:${categoryId}:${row.id}`)}
                onStartEdit={() => startEditing(`split-amount:${categoryId}:${row.id}`)}
                onCancel={stopEditing}
                advanceOnEnter
                onCommit={(raw, intent) => {
                  const amount = parseAmountInput(raw);
                  if (amount !== null) {
                    dispatch({
                      type: 'APPLY_OP',
                      op: (document) => setSplitRowAmount(document, categoryId, row.id, amount),
                    });
                  }
                  advanceRows('amount', row.id, intent);
                }}
                align="right"
                inputWidth={90}
                inputSx={{ fontFamily: 'var(--font-family-mono)', fontSize: 11, fontWeight: 500 }}
                display={
                  <Box
                    component="span"
                    sx={{ fontFamily: 'var(--font-family-mono)', fontSize: 11, fontWeight: 500 }}
                  >
                    {formatAmountAbbrev(Math.round(row.amount))}
                  </Box>
                }
              />
              <Box
                component="span"
                data-automation-id="split-row-pct"
                sx={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 11,
                  color: 'var(--fg-3)',
                  bgcolor: 'var(--bg-panel)',
                  px: 1,
                  py: 0.5,
                  borderRadius: 'var(--border-radius-pill)',
                  flex: 'none',
                }}
              >
                {row.sharePct}%
              </Box>
              <Box
                component="button"
                type="button"
                data-automation-id="split-row-remove"
                onClick={() =>
                  dispatch({
                    type: 'APPLY_OP',
                    op: (document) => removeSplitRow(document, categoryId, row.id),
                  })
                }
                sx={{
                  width: 14,
                  height: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  bgcolor: 'transparent',
                  color: 'var(--fg-3)',
                  flex: 'none',
                  p: 0,
                  cursor: 'pointer',
                  '&:hover': { color: 'var(--color-coral-80)' },
                }}
              >
                <MaterialSymbol name="close" size={12} />
              </Box>
            </Box>
          ))}

          <Box
            component="button"
            type="button"
            data-automation-id={`split-add-row-${categoryId}`}
            onClick={() =>
              dispatch({
                type: 'APPLY_OP',
                op: (document) =>
                  addSplitRow(document, categoryId, {
                    id: crypto.randomUUID(),
                    name:
                      categoryId === 'exp'
                        ? t('splitRows.DefaultExpense')
                        : t('splitRows.DefaultType'),
                  }),
              })
            }
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.5,
              mt: 1,
              border: 'var(--border-width-thin) dashed var(--border-divider)',
              borderRadius: 'var(--border-radius-pill)',
              bgcolor: 'transparent',
              fontSize: 11,
              fontFamily: 'var(--font-family)',
              color: 'var(--fg-3)',
              cursor: 'pointer',
              '&:hover': { borderColor: accent, color: 'var(--fg-1)' },
            }}
          >
            <MaterialSymbol name="add" size={12} />
            {t('splitRows.AddRow')}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
